import { useEffect, useState } from 'react';
import {
  clearAxiosHeader,
  setAxiosHeader,
  setAxiosInterceptors,
} from '../../../modules/api/axiosInterceptors';
import { useRouter } from 'next/router';
import { getBroadcastChannel } from 'src/modules/broadcastChannel/broadcastChannel';
import SSOConf from '@/constants/SSOConf';
import appStorage from '@/modules/storage/appStorage';
import getMe from '@/api/auth/getMe.endpoint';
import { usePlausibleContext } from '../PlausibleAnalytics/PlausibleAnalytics';
import usePaymentFlowAnalytics from '@/v2/domains/Analytics/hooks/usePaymentFlowAnalytics.hook';
import {
  PaymentSection,
  PaymentFlowStep,
} from '@/v2/domains/Analytics/types/payment';

import { useQueryClient } from '@tanstack/react-query';
import isSubscriptionAboutToExpire from '@/v2/domains/Subscriptions/utils/isSubscriptionAboutToExpire';
import { QUERY_KEYS } from '@/v2/constants/QueryKeys';

const contextName = 'globalContext';
export const globalContextChannel = contextName;

export default function useGlobalContext() {
  const router = useRouter();
  const [state, setState] = useState({
    loaded: false,
    accessToken: null,
    user: null,
    impersonatedBy: [],
    isSubscriptionActive: false,
    isSubscriptionAboutToExpire: false,
    isTeacherPro: false,
    isTeacher: false,
    subscription: null,
    nextUrl: null,
    availableLanguages: ['es'],
    selectedLanguage: 'es',
    alertsCount: 0,
  });
  const [showSessionModal, setShowSessionModal] = useState(false);
  const plausibleContext = usePlausibleContext();

  const { onStepViewed } = usePaymentFlowAnalytics({
    section: PaymentSection.eligibility,
    step: PaymentFlowStep.subscriptionExpiredDetected,
  });

  const queryClient = useQueryClient();
  const setMeQuery = (user) => {
    queryClient.setQueryData([QUERY_KEYS.ME], user);
  };

  const actions = {
    setState: (newState) => {
      setState((previousState) => {
        const newCombinedState = { ...previousState, ...newState };
        appStorage('localStorage').setItem(
          contextName,
          JSON.stringify(newCombinedState)
        );
        return newCombinedState;
      });
    },
    publicActions: {
      refreshUser: async () => {
        actions.setState({ pendingRefreshSession: false });
        try {
          const user = await getMe();
          setMeQuery(user);
          actions.setState({
            user,
            ...getSubscriptionState(user),
          });
        } catch (e) {
          console.log('error', e);
        }
      },
      setSession: ({ accessToken, user, impersonatedBy, nextUrl }) => {
        setAxiosHeader(accessToken);
        setMeQuery(user);
        const subscriptionState = getSubscriptionState(user);
        const sessionState = {
          accessToken,
          user,
          impersonatedBy,
          loading: false,
          nextUrl,
          alertsCount: 0,
          ...subscriptionState,
        };
        actions.setState(sessionState);

        if (
          subscriptionState.subscription &&
          !subscriptionState.isSubscriptionActive
        ) {
          onStepViewed({
            calculationData: {
              trial: subscriptionState.subscription.trial,
            },
          });
        }

        const channel = getBroadcastChannel(globalContextChannel);

        channel.postMessage({ message: 'NEW_SESSION', user });
      },
      clearSession: (notify = true) => {
        clearAxiosHeader();
        setShowSessionModal(false);
        actions.setState({
          accessToken: null,
          user: null,
          impersonatedBy: [],
          isSubscriptionActive: false,
          isSubscriptionAboutToExpire: false,
          isTeacherPro: false,
          isTeacher: false,
          subscription: null,
          nextUrl: null,
          alertsCount: 0,
        });
        if (notify) {
          const channel = getBroadcastChannel(globalContextChannel);
          channel.postMessage({ message: 'LOGOUT' });
        }
      },
      setSelectedLanguage: (selectedLanguage) => {
        actions.setState({ selectedLanguage });
      },
      manageExpiredSession: () => {
        if (state.user?.teacher || state.user?.student) {
          setShowSessionModal(true);
        } else {
          router.push(
            `/logout?errorMessage=${encodeURIComponent(
              'Your session has expired'
            )}`
          );
        }
      },
      refreshSession: async () => {
        const user = state.user?.student || state.user?.teacher;
        const externalSsoProvider = user?.ssoProviders?.find(
          (ssoProvider) => ssoProvider.provider != 'password'
        );

        let loginUrl = null;
        setShowSessionModal(false);
        if (externalSsoProvider) {
          loginUrl = SSOConf[externalSsoProvider.provider].getLoginUrl({
            isStudent: !!state.user.student,
          });
          router.push(loginUrl);
        } else {
          router.push(
            `/login?userType=${
              state.user ? (state.user.teacher ? 'teacher' : 'student') : ''
            }`
          );
          actions.publicActions.clearSession();
        }
      },
      closeModal: () => {
        setShowSessionModal(false);
        router.push(
          `/login?userType=${
            state.user ? (state.user.teacher ? 'teacher' : 'student') : ''
          }`
        );
        actions.publicActions.clearSession();
      },
      hasPermission: (permission) => {
        const user =
          state.user?.teacher || state.user?.student || state.user?.admin;
        return user?.data?.permissions?.some(
          (userPermission) =>
            userPermission == permission || userPermission == 'all'
        );
      },
      setAlertsCount: (alertsCount) => {
        actions.setState({ alertsCount });
      },
    },
  };

  useEffect(() => {
    const savedState = appStorage('localStorage').getItem(contextName);
    if (savedState) {
      const savedStateObject = JSON.parse(savedState);
      setAxiosHeader(savedStateObject.accessToken);
      actions.setState({
        ...savedStateObject,
        loaded: true,
        pendingRefreshSession: true,
      });
    } else {
      actions.setState({ loaded: true });
    }
    setShowSessionModal(false);
  }, []);

  useEffect(() => {
    if (state.pendingRefreshSession && state.user) {
      actions.publicActions.refreshUser();
    }
  }, [state.pendingRefreshSession]);

  useEffect(() => {
    setAxiosInterceptors(actions.publicActions);
  }, [state.user]);

  useEffect(() => {
    function onGlobalContextMessage(data) {
      const logoutReceived = data.message == 'LOGOUT';
      const newSessionReceived = data.message == 'NEW_SESSION';
      let isSameUser = false;
      if (newSessionReceived) {
        if (data.user.teacher) {
          isSameUser = data.user.teacher.id == state.user?.teacher?.id;
        } else if (data.user.student) {
          isSameUser = data.user.student.id == state.user?.student?.id;
        } else if (data.user.admin) {
          isSameUser = data.user.admin?.id == state.user?.admin?.id;
        }
      }
      if (logoutReceived || (newSessionReceived && !isSameUser)) {
        actions.publicActions.clearSession(false);
        router.push('/login');
      }
    }

    const channel = getBroadcastChannel(globalContextChannel);
    channel.addEventListener('message', onGlobalContextMessage);

    return () => {
      channel.removeEventListener('message', onGlobalContextMessage);
    };
  }, [state.user]);

  useEffect(() => {
    if (state.loaded) {
      const user =
        state.user?.teacher || state.user?.student || state.user?.admin;

      const newState = {
        userName: user?.firstName ? `${user.firstName} ${user.lastName}` : '',
      };
      if (state.user?.student) {
        const availableLanguages = getAvailableLanguages(
          state.user?.student?.subscriptions
        );
        newState.availableLanguages = availableLanguages;
      }

      if (!state.user) {
        newState.availableLanguages = ['es'];
      }

      actions.setState(newState);
    }
  }, [state.loaded, state.user]);

  useEffect(() => {
    if (
      state.availableLanguages?.length &&
      !state.availableLanguages.includes(state.selectedLanguage)
    ) {
      actions.setState({ selectedLanguage: state.availableLanguages[0] });
    }
  }, [state.availableLanguages]);

  useEffect(() => {
    plausibleContext.actions.setGlobalContextState(state);
  }, [state.loaded, state.user]);

  return {
    state: {
      ...state,
      showSessionModal,
    },
    actions: actions.publicActions,
  };
}

function getSubscriptionState(user) {
  let subscriptionState = {};
  if (user?.teacher) {
    const { subscription } = user.teacher;
    if (subscription) {
      const availableLanguages = getAvailableLanguages([subscription]);

      const isAboutToExpire = isSubscriptionAboutToExpire(subscription);

      subscriptionState = {
        subscription,
        isSubscriptionActive: subscription?.status == 'active',
        isSubscriptionAboutToExpire: isAboutToExpire,
        isTeacherPro: subscription?.subscriptionType?.code == 'teacherPro',
        isTeacher: subscription?.subscriptionType?.code == 'teacher',
        availableLanguages,
      };
    } else {
      subscriptionState = {
        subscription: null,
        isSubscriptionActive: false,
        isSubscriptionAboutToExpire: false,
        isTeacherPro: false,
        availableLanguages: ['es'],
      };
    }
  }
  return subscriptionState;
}

function getAvailableLanguages(subscriptions = []) {
  const availableLanguages = [];
  for (const subscription of subscriptions) {
    const subscriptionLanguages =
      subscription.language == 'all' ? ['es', 'fr'] : [subscription.language];
    for (const language of subscriptionLanguages) {
      if (!availableLanguages.includes(language)) {
        availableLanguages.push(language);
      }
    }
  }
  return availableLanguages;
}
